<template>
  <tr
    :id="`inspection-details-${row.id}`"
    class="inspection-details-tr"
  >
    <td
      colspan="100%"
      class="py-0"
    >
      <div class="row">
        <div
          v-if="!isPeriodic"
          class="col-12 col-md-5"
        >
          <div class="content">
            <div class="title">
              Access Instructions
            </div>
            <div class="mb-3">
              <span class="font-weight-bold">Lockbox / Keypad Code:</span>
              <span>
                {{ row.lock_box_code }}
                <q-icon
                  name="edit"
                  size="16px"
                  color="primary"
                />
                <q-popup-edit
                  v-model="row.lock_box_code"
                  @save="(value, oldValue)=> value !== oldValue && updateInspection({lock_box_code: value || null})"
                >
                  <q-input
                    v-model="row.lock_box_code"
                    autofocus
                  />
                </q-popup-edit>
              </span>
              <SdCopyToClipboard
                with-icon
                :copy="row.lock_box_code"
                class="ml-2"
              />
            </div>
            <div>
              <span class="font-weight-bold">Instructions:</span>
              <span>
                {{ row.access_instructions }}
                <q-icon
                  name="edit"
                  size="16px"
                  color="primary"
                />
                <q-popup-edit
                  v-model="row.access_instructions"
                  class="inspection-details-textarea-popup"
                  buttons
                  @save="(value, oldValue)=> value !== oldValue && updateInspection({access_instructions: value || null})"
                >
                  <q-input
                    v-model="row.access_instructions"
                    type="textarea"
                    autofocus
                    @keyup.enter.stop
                  />
                </q-popup-edit>
              </span>
              <SdCopyToClipboard
                with-icon
                :copy="row.access_instructions"
                class="ml-2"
              />
            </div>
          </div>
        </div>
        <div
          v-if="isPeriodic"
          class="col-12 col-md-3"
        >
          <div class="content">
            <div class="title">
              Tenants
            </div>
            <template v-if="row.tenants && row.tenants.length">
              <q-chip
                v-for="(tenant, index) in row.tenants"
                :key="`tenant_${index}`"
                dense
                clickable
                class="p-1 w-100 mb-2"
              >
                <div class="float-left">
                  <SdCopyToClipboard
                    v-tippy
                    :copy="`${tenant.full_name}, ${tenant.formatted_phone_number},  ${tenant.email}`"
                    :with-icon="true"
                    :title="`${tenant.full_name}, ${tenant.formatted_phone_number},  ${tenant.email}`"
                  >
                    <span
                      class="font-weight-bold mr-2"
                    >{{ tenant.full_name }}</span>
                  </SdCopyToClipboard>
                </div>
                <div class="float-right">
                  <SdCopyToClipboard
                    v-if="tenant.formatted_phone_number"
                    :copy="tenant.formatted_phone_number"
                  >
                    <q-icon
                      v-tippy
                      name="phone"
                      :title="tenant.formatted_phone_number"
                    />
                    <q-icon name="content_copy" />
                  </SdCopyToClipboard>
                  <SdCopyToClipboard
                    v-if="tenant.email"
                    :copy="tenant.email"
                  >
                    <q-icon
                      v-tippy
                      name="email"
                      :title="tenant.email"
                    />
                    <q-icon name="content_copy" />
                  </SdCopyToClipboard>
                  <edit-inspection-tenant
                    :tenant="tenant"
                    @save="updateTenant"
                  />
                  <q-icon
                    v-if="index > 0"
                    name="delete"
                    size="16px"
                    color="red"
                    class="cursor-pointer ml-2"
                    @click.native="deleteTenant(index)"
                  />
                </div>
              </q-chip>
            </template>
            <edit-inspection-tenant
              class="d-block cursor-pointer"
              @save="updateTenant"
            >
              Add
            </edit-inspection-tenant>
            <div
              v-if="showSchedulingLink"
              class="my-2"
            >
              Tenant scheduling link:
              <a
                :href="row.tenant_scheduling_url.startsWith('http') ? row.tenant_scheduling_url : `//${row.tenant_scheduling_url}`"
                target="_blank"
              >
                {{ row.tenant_scheduling_url }}
              </a>
              <SdCopyToClipboard
                with-icon
                class="ml-2"
                :copy="row.tenant_scheduling_url"
              />
            </div>
          </div>
        </div>
        <div
          class="col-12"
          :class="isPeriodic ? 'col-md-7': 'col-md-5'"
        >
          <div class="content">
            <div class="title">
              Additional notes
              <p
                v-if="this.row.type === this.$config.inspectionTypes.MOVE_IN_OUT"
                class="floor-plan-details"
              >
                {{ floorPlanDetails }}
              </p>
            </div>
            <span class="pre-line">
              {{ row.instructions }}
              <q-icon
                name="edit"
                size="16px"
                color="primary"
              />
              <q-popup-edit
                v-model="row.instructions"
                buttons
                class="inspection-details-textarea-popup"
                @save="(value, oldValue)=> value !== oldValue && updateInspection({instructions: value || null})"
              >
                <q-input
                  v-model="row.instructions"
                  type="textarea"
                  autofocus
                  @keyup.enter.stop
                />
              </q-popup-edit>
            </span>
            <SdCopyToClipboard
              with-icon
              :copy="row.instructions"
              class="ml-2"
            />
          </div>
        </div>
        <div class="col-12 col-md-2">
          <div class="content">
            <div class="title">
              Pricing
            </div>
            <div class="mb-3">
              Pm payment: ${{ row.price }}
            </div>
            <div class="mb-3">
              Agent payout: ${{ row.agent_payout }}
              <span v-if="!transactions.transfer">
                <q-icon
                  name="edit"
                  size="16px"
                  color="primary"
                />
                <q-popup-edit
                  v-model="row.agent_payout"
                  @save="(value, oldValue) => value !== oldValue && updateInspection({agent_payout: value || 0})"
                  @hide="checkValue(row.agent_payout, row)"
                >
                  <q-input
                    v-model="row.agent_payout"
                    type="number"
                    autofocus
                  />
                </q-popup-edit>
              </span>
              <router-link
                v-if="transactions.transfer"
                :to="{ name: 'app.transactions', params: { id: transactions.transfer.id } }"
                class="q-mr-sm"
              >
                #{{ transactions.transfer.id }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </td>
  </tr>
</template>

<script>
import SdCopyToClipboard from './common/SdCopyToClipboard';
import EditInspectionTenant from './edit-inspection-tenant';

export default {
  name: 'InspectionDetails',
  components: {
    EditInspectionTenant,
    SdCopyToClipboard,
  },
  props: {
    row: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isPeriodic() {
      return this.row.type === this.$config.inspectionTypes.PERIODIC;
    },
    floorPlanDetails() {
      const { bedrooms, bathrooms, size } = this.row.unit;
      let details = bedrooms ? `${bedrooms}bd/` : '';
      details += bathrooms ? `${bathrooms}ba/` : '';
      details += size ? `${size}sqft` : '';
      return details;
    },
    showSchedulingLink() {
      const inspectionStates = this.$config.inspectionStates;
      return this.row.tenant_scheduling_url
        && [
          inspectionStates.WAITING_FOR_TENANT.value,
          inspectionStates.SCHEDULED.value,
          inspectionStates.ON_HOLD_TENANT.value,
          inspectionStates.ON_HOLD.value,
        ].includes(this.row.state);
    },
    transactions() {
      const transactions = {
        charge: null,
        transfer: null,
      };
      if (!this.row.transactions || !this.row.transactions.length) {
        return transactions;
      }
      const chargeType = this.$config.transactionTypes.INSPECTION_CHARGE;
      const transferType = this.$config.transactionTypes.INSPECTION_TRANSFER;
      transactions.charge = this.row.transactions.find((transaction) => transaction.type === chargeType);
      transactions.transfer = this.row.transactions.find((transaction) => transaction.type === transferType);
      return transactions;
    },
  },
  methods: {
    updateInspection(data) {
      this.api().request('PATCH', `admin/inspections/${this.row.id}`, data)
        .catch((error) => {
          let { message } = error;
          if (error.errors) {
            message = `Invalid ${Object.keys(error.errors).join(', ')}`;
          }
          this.uiNotify(message || 'Error occurred while updating condition report', 'negative');
        });
    },
    checkValue(value, inspection) {
      if (!value) {
        value = 0;
        if (inspection.agent_payout === null) {
          inspection.agent_payout = value;
          this.updateInspection({ agent_payout: value });
        } else if (inspection.price === null) {
          inspection.price = value;
          this.updateInspection({ price: value });
        }
      }
    },
    updateTenant(newTenant) {
      let request;
      const isUpdate = newTenant.id;
      if (isUpdate) {
        request = this.api().request('PUT', `admin/inspections/${this.row.id}/tenants/${newTenant.id}`, newTenant);
      } else {
        request = this.api().request('POST', `admin/inspections/${this.row.id}/tenants`, newTenant);
      }

      request.then((response) => {
        if (isUpdate) {
          const tenantIndex = this.row.tenants.findIndex((tenant) => tenant.id === response.id);
          this.row.tenants[tenantIndex] = response;
          this.row.tenants = [...this.row.tenants]; // trigger observable update
        } else {
          this.row.tenants.push(response);
        }
      }).catch((error) => {
        let { message } = error;
        if (error.errors) {
          message = `Invalid ${Object.keys(error.errors).join(', ')}`;
        }
        this.uiNotify(message || 'Error occurred while updating condition report', 'negative');
      });
    },
    async deleteTenant(index) {
      const tenant = this.row.tenants[index];
      try {
        await this.uiConfirm(`Delete tenant ${tenant.full_name}?`);
      } catch (error) {
        return;
      }

      try {
        await this.api().request('DELETE', `admin/inspections/${this.row.id}/tenants/${tenant.id}`);
        this.row.tenants.splice(index, 1);
        this.row.tenants = [...this.row.tenants]; // trigger observable update
      } catch (error) {
        this.uiNotify(error.message || 'Error occurred while deleting tenant', 'negative');
      }
    },
  },
};
</script>

<style lang="scss">
.inspection-details-tr {
    td {
        background: #DDDDDD;
        white-space: normal;
        .row {
            flex: 1;

            > .col-12 {
                padding: 1rem 0.75rem;

                .content {
                    padding: 0.75rem;
                    background: white;
                    font-size: 14px;
                    height: 100%;

                    .title {
                        font-size: 15px;
                        font-weight: bold;
                        margin-bottom: 1rem;
                        .floor-plan-details {
                          font-size: 14px;
                          font-weight: 400;
                        }
                    }
                }
            }
        }

    }
}
.inspection-details-textarea-popup {
  width: 450px;
}
</style>
